import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { appSessionResetEventName } from '../utils/resetAppSession';
import settingsService from '../services/settingsService';

const FilterContext = createContext();

const DEFAULT_FILTER_STATE = {
  dateFilter: 'Today',
  customDateRange: null,
  teamFilter: 'all',
  memberFilter: '',
};

const DATE_FILTER_TO_PARAM = {
  Today: 'today',
  Yesterday: 'yesterday',
  'This Week': 'this-week',
  'Last Week': 'last-week',
  'This Month': 'this-month',
  'Last Month': 'last-month',
  'Last 7 Days': 'last-7-days',
  Custom: 'custom',
};

const PARAM_TO_DATE_FILTER = Object.fromEntries(
  Object.entries(DATE_FILTER_TO_PARAM).map(([label, value]) => [value, label])
);

const FILTER_QUERY_KEYS = ['period', 'from', 'to', 'team', 'member', 'user'];

const ADMIN_WORKSPACE_PATHS = new Set([
  '/',
  '/overview',
  '/my-teams',
  '/screenshots',
  '/timelapse-videos',
  '/timesheet',
  '/risk-user-history',
  '/timeline',
  '/manual-time',
  '/real-time-alert',
  '/apply-leave',
  '/leave-summary',
  '/manage-leave',
  '/manage-holiday',
  '/review-usage',
  '/apps-summary',
  '/downloads',
  '/account-settings',
  '/faq',
]);

const ADMIN_WORKSPACE_PREFIXES = ['/member/', '/reports', '/payroll', '/apps-history', '/settings'];

export const isFilterableWorkspacePath = (pathname = '') => {
  if (pathname.startsWith('/user/')) return true;
  if (ADMIN_WORKSPACE_PATHS.has(pathname)) return true;
  return ADMIN_WORKSPACE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
};

const hasAuthToken = () => {
  try {
    return Boolean(localStorage.getItem('token'));
  } catch (_) {
    return false;
  }
};

const parseDateValue = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return date;
};

const toDateValue = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const hasFilterQueryParams = (search = '') => {
  const params = new URLSearchParams(search);
  return FILTER_QUERY_KEYS.some((key) => params.has(key));
};

export const parseFilterSearch = (search = '') => {
  const params = new URLSearchParams(search);
  let dateFilter = PARAM_TO_DATE_FILTER[params.get('period')] || 'Today';
  let customDateRange = null;

  if (dateFilter === 'Custom') {
    const start = parseDateValue(params.get('from'));
    const end = parseDateValue(params.get('to'));
    if (start && end && start.getTime() <= end.getTime()) {
      customDateRange = { start, end };
    } else {
      dateFilter = 'Today';
    }
  }

  const rawTeam = (params.get('team') || '').trim();
  const teamFilter = rawTeam && rawTeam.toLocaleLowerCase() !== 'all' ? rawTeam : 'all';

  const memberFilter = (params.get('member') || params.get('user') || '').trim();
  return { dateFilter, customDateRange, teamFilter, memberFilter };
};

export const serializeFilterSearch = (search = '', state = DEFAULT_FILTER_STATE) => {
  const params = new URLSearchParams(search);
  FILTER_QUERY_KEYS.forEach((key) => params.delete(key));

  const dateFilter = DATE_FILTER_TO_PARAM[state.dateFilter] ? state.dateFilter : 'Today';
  if (dateFilter !== 'Today') {
    if (dateFilter === 'Custom') {
      const from = toDateValue(state.customDateRange?.start);
      const to = toDateValue(state.customDateRange?.end);
      if (from && to) {
        params.set('period', 'custom');
        params.set('from', from);
        params.set('to', to);
      }
    } else {
      params.set('period', DATE_FILTER_TO_PARAM[dateFilter]);
    }
  }

  if (state.teamFilter !== null && state.teamFilter !== undefined && String(state.teamFilter) !== 'all') {
    params.set('team', String(state.teamFilter));
  }

  if (state.memberFilter) params.set('member', String(state.memberFilter));

  const query = params.toString();
  return query ? `?${query}` : '';
};

const serializeFilterSearchForPath = (pathname = '', search = '', state = DEFAULT_FILTER_STATE) => (
  serializeFilterSearch(search, {
    ...state,
    memberFilter: pathname === '/my-teams' ? '' : state.memberFilter,
  })
);

export const formatDateFilterLabel = (dateFilter, customDateRange) => {
  if (dateFilter !== 'Custom' || !customDateRange?.start) {
    return dateFilter || 'Today';
  }
  const start = new Date(customDateRange.start);
  const end = customDateRange.end ? new Date(customDateRange.end) : start;

  const startDay = start.getDate();
  const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
  const startYear = start.getFullYear();

  const endDay = end.getDate();
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
  const endYear = end.getFullYear();

  if (startDay === endDay && start.getMonth() === end.getMonth() && startYear === endYear) {
    return `${startDay} ${startMonth} ${startYear}`;
  }

  if (startYear === endYear) {
    return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${startYear}`;
  }

  return `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`;
};

const getInitialFilterState = (pathname, search) => {
  if (!hasAuthToken() || !isFilterableWorkspacePath(pathname)) {
    return DEFAULT_FILTER_STATE;
  }
  return parseFilterSearch(search);
};

const FALLBACK_FILTER_CONTEXT = {
  dateFilter: 'Today',
  setDateFilter: () => {},
  customDateRange: null,
  setCustomDateRange: () => {},
  formatDateFilterLabel: (df, range) => formatDateFilterLabel(df, range),
  teamFilter: 'all',
  setTeamFilter: () => {},
  teamFilterLabel: 'All Teams',
  teamFilterId: null,
  memberFilter: '',
  setMemberFilter: () => {},
  teams: [],
  teamsLoading: false,
  teamsError: '',
  refreshTeams: () => Promise.resolve(),
  getDateRange: (filter = 'Today', customRange = null) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfDay = (date) => {
      const d = new Date(date);
      d.setHours(23, 59, 59, 999);
      return d;
    };
    switch (filter) {
      case 'Yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { start: yesterday, end: endOfDay(yesterday) };
      }
      case 'This Week': {
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        return { start: startOfWeek, end: now };
      }
      case 'Last Week': {
        const lastWeekStart = new Date(today);
        lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekEnd.getDate() + 6);
        return { start: lastWeekStart, end: endOfDay(lastWeekEnd) };
      }
      case 'This Month': {
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        return { start: startOfMonth, end: now };
      }
      case 'Last Month': {
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        return { start: lastMonthStart, end: endOfDay(lastMonthEnd) };
      }
      case 'Custom': {
        if (customRange?.start && customRange?.end) {
          const start = new Date(customRange.start);
          start.setHours(0, 0, 0, 0);
          const end = new Date(customRange.end);
          end.setHours(23, 59, 59, 999);
          return { start, end };
        }
        if (customRange?.start) {
          const start = new Date(customRange.start);
          start.setHours(0, 0, 0, 0);
          return { start, end: endOfDay(start) };
        }
        return { start: today, end: endOfDay(today) };
      }
      default:
        return { start: today, end: endOfDay(today) };
    }
  },
  headerSearch: '',
  setHeaderSearch: () => {},
};

export const useFilters = () => {
  const context = useContext(FilterContext);
  return context || FALLBACK_FILTER_CONTEXT;
};

export const FilterProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialFilterStateRef = useRef(null);
  if (initialFilterStateRef.current === null) {
    initialFilterStateRef.current = getInitialFilterState(location.pathname, location.search || '');
  }
  const [dateFilter, setDateFilter] = useState(initialFilterStateRef.current.dateFilter);
  const [customDateRange, setCustomDateRange] = useState(initialFilterStateRef.current.customDateRange);
  const [teamFilter, updateTeamFilter] = useState(initialFilterStateRef.current.teamFilter);
  const [memberFilter, setMemberFilter] = useState(initialFilterStateRef.current.memberFilter || '');
  const setTeamFilter = useCallback((value) => {
    updateTeamFilter(value);
    setMemberFilter('');
  }, []);
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsError, setTeamsError] = useState('');
  const [headerSearch, setHeaderSearch] = useState('');
  const currentLocationRef = useRef(location);
  const previousLocationRef = useRef({
    pathname: location.pathname,
    search: location.search || '',
  });
  const applyingUrlStateRef = useRef(false);
  currentLocationRef.current = location;

  const fetchTeams = useCallback(async () => {
    if (process.env.NODE_ENV === 'test' && !window.__ALLOW_FILTER_TEAM_FETCH__) {
      setTeams([]);
      setTeamsLoading(false);
      setTeamsError('');
      return;
    }
    if (!localStorage.getItem('token')) {
      setTeams([]);
      setTeamsLoading(false);
      setTeamsError('');
      return;
    }
    const pathname = location.pathname || '';
    if (!isFilterableWorkspacePath(pathname)) {
      setTeamsLoading(false);
      setTeamsError('');
      return;
    }
    // Employee pages share the date-filter state, but they do not expose the
    // organization team picker. /api/teams/ is intentionally admin-only, so
    // requesting it here creates a noisy 403 loop for every employee route.
    if (pathname.startsWith('/user/')) {
      setTeams([]);
      setTeamFilter('all');
      setTeamsLoading(false);
      setTeamsError('');
      return;
    }
    setTeamsLoading(true);
    setTeamsError('');
    try {
      const data = await settingsService.getTeams();
      const list = Array.isArray(data) ? data : data?.results || [];
      setTeams(list);
    } catch (err) {
      console.error('FilterContext: failed to load teams', err);
      setTeams([]);
      setTeamsError('Team filters are temporarily unavailable.');
    } finally {
      setTeamsLoading(false);
    }
  }, [location.pathname, setTeamFilter]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  useEffect(() => {
    const current = {
      pathname: location.pathname || '',
      search: location.search || '',
    };
    const previous = previousLocationRef.current;
    previousLocationRef.current = current;

    if (!hasAuthToken() || !isFilterableWorkspacePath(current.pathname)) return;

    const pathnameChanged = previous.pathname !== current.pathname;
    const searchChanged = previous.search !== current.search;
    if (!pathnameChanged && !searchChanged) return;

    const urlContainsFilters = hasFilterQueryParams(current.search);
    if (searchChanged && (!pathnameChanged || urlContainsFilters)) {
      const parsed = parseFilterSearch(current.search);
      const dateChanged = parsed.dateFilter !== dateFilter;
      const teamChanged = String(parsed.teamFilter) !== String(teamFilter);
      const currentStart = toDateValue(customDateRange?.start);
      const currentEnd = toDateValue(customDateRange?.end);
      const nextStart = toDateValue(parsed.customDateRange?.start);
      const nextEnd = toDateValue(parsed.customDateRange?.end);
      const customRangeChanged = currentStart !== nextStart || currentEnd !== nextEnd;
      const memberChanged = parsed.memberFilter !== memberFilter;
      const filterStateChanged = dateChanged || teamChanged || customRangeChanged || memberChanged;

      if (filterStateChanged) {
        applyingUrlStateRef.current = true;
        if (dateChanged) setDateFilter(parsed.dateFilter);
        if (teamChanged) updateTeamFilter(parsed.teamFilter);
        if (memberChanged) setMemberFilter(parsed.memberFilter);
        if (customRangeChanged) setCustomDateRange(parsed.customDateRange);
      }
      return;
    }

    // Normal route links do not carry query strings. Keep the active global
    // filters and replace the new route with its shareable equivalent.
    if (pathnameChanged && !urlContainsFilters) {
      const nextSearch = serializeFilterSearchForPath(current.pathname, current.search, {
        dateFilter,
        customDateRange,
        teamFilter,
        memberFilter,
      });
      if (nextSearch !== current.search) {
        navigate({ pathname: current.pathname, search: nextSearch }, { replace: true });
      }
    }
  }, [customDateRange, dateFilter, location.pathname, location.search, navigate, teamFilter, memberFilter]);

  useEffect(() => {
    if (applyingUrlStateRef.current) {
      applyingUrlStateRef.current = false;
      return;
    }

    const current = currentLocationRef.current;
    const pathname = current.pathname || '';
    if (!hasAuthToken() || !isFilterableWorkspacePath(pathname)) return;

    const currentSearch = current.search || '';
    const nextSearch = serializeFilterSearchForPath(pathname, currentSearch, {
      dateFilter,
      customDateRange,
      teamFilter,
      memberFilter,
    });
    if (nextSearch !== currentSearch) {
      navigate({ pathname, search: nextSearch }, { replace: true });
    }
  }, [customDateRange, dateFilter, navigate, teamFilter, memberFilter]);

  useEffect(() => {
    const handleReset = () => {
      setDateFilter('Today');
      setCustomDateRange(null);
      setTeamFilter('all');
      setTeams([]);
      setTeamsLoading(false);
      setTeamsError('');
      setHeaderSearch('');
    };
    const handleUserUpdated = (e) => {
      if (e.detail?.id) fetchTeams();
    };
    window.addEventListener(appSessionResetEventName, handleReset);
    window.addEventListener('userUpdated', handleUserUpdated);
    return () => {
      window.removeEventListener(appSessionResetEventName, handleReset);
      window.removeEventListener('userUpdated', handleUserUpdated);
    };
  }, [fetchTeams, setTeamFilter]);

  const teamFilterLabel = teamFilter === 'all'
    ? 'All Team'
    : (teams.find((t) => String(t.id) === String(teamFilter))?.name || 'All Team');

  const teamFilterId = teamFilter === 'all' ? null : teamFilter;

  const getDateRange = useCallback((filter) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfDay = (date) => {
      const d = new Date(date);
      d.setHours(23, 59, 59, 999);
      return d;
    };
    
    switch (filter) {
      case 'Today':
        return {
          start: today,
          end: endOfDay(today)
        };
      
      case 'Yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return {
          start: yesterday,
          end: endOfDay(yesterday)
        };
      
      case 'This Week':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        return {
          start: startOfWeek,
          end: now
        };
      
      case 'Last Week':
        const lastWeekStart = new Date(today);
        lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekEnd.getDate() + 6);
        return {
          start: lastWeekStart,
          end: endOfDay(lastWeekEnd)
        };
      
      case 'This Month':
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        return {
          start: startOfMonth,
          end: now
        };
      
      case 'Last Month':
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        return {
          start: lastMonthStart,
          end: endOfDay(lastMonthEnd)
        };
      
      case 'Last 7 Days':
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 6);
        return {
          start: sevenDaysAgo,
          end: endOfDay(today)
        };

      case 'Custom':
        if (customDateRange?.start && customDateRange?.end) {
          const start = new Date(customDateRange.start);
          start.setHours(0, 0, 0, 0);
          const end = new Date(customDateRange.end);
          end.setHours(23, 59, 59, 999);
          return { start, end };
        }
        if (customDateRange?.start) {
          const start = new Date(customDateRange.start);
          start.setHours(0, 0, 0, 0);
          return { start, end: endOfDay(start) };
        }
        const customStart = new Date(today);
        customStart.setDate(today.getDate() - 7);
        return { start: customStart, end: now };

      default:
        return {
          start: new Date(0),
          end: new Date()
        };
    }
  }, [customDateRange]);

  return (
    <FilterContext.Provider 
      value={{ 
        dateFilter, 
        setDateFilter, 
        customDateRange,
        setCustomDateRange,
        formatDateFilterLabel,
        teamFilter, 
        setTeamFilter,
        teamFilterLabel,
        teamFilterId,
        memberFilter,
        setMemberFilter,
        teams,
        teamsLoading,
        teamsError,
        refreshTeams: fetchTeams,
        getDateRange,
        headerSearch,
        setHeaderSearch
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};
